import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, NotePencil, ListChecks, Flag, ScribbleLoop, Compass, FilePdf } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { CONTINUOUS_DYNAMICS } from "@/components/method/continuousDynamics";
import ProgressivePromptsStrip from "./ProgressivePromptsStrip";
import CurrentReflectionBlock from "./CurrentReflectionBlock";
import WorkingJournalStream from "./WorkingJournalStream";
import { GROUPING_ORDER } from "./reflectionGroupings";

/**
 * ReflectionCanvasArticle — Round-4 editorial reflection surface.
 *
 * Extracted from EngagementEmbraceTab.jsx during Phase-A hardening.
 * Behavior, endpoints, styling, and `data-testid`s are preserved verbatim
 * so the existing test suite continues to pass.
 *
 * Renders as a full editorial article inside the map container with three
 * canonical layers of writing:
 *
 *   1. Framing        — canonical, read-only (from Method template)
 *   2. Current Reflection — GOVERNED · exactly ONE per (engagement, node).
 *        Updating snapshots the prior into reflection_history[].
 *   3. Working Journal — PROVISIONAL · fluid stream. Cheap to write,
 *        visibility-controlled (private / team / firm). Promote lifts an
 *        entry into the Current Reflection.
 *
 * Endpoints:
 *   POST   /engagements/{id}/roadmap/{phase|lens}/{key}/reflection
 *   PATCH  /engagements/{id}/roadmap/{phase|lens}/{key}/current-reflection
 *   PATCH  /engagements/{id}/roadmap/{phase|lens}/{key}/reflection/{rid}/visibility
 */
export default function ReflectionCanvasArticle({
  pinnedNode, roadmap, setRoadmap, engagementId, clientId, currentUser,
  onClose, onOpenBriefing,
  signals, signalsMovement, setSignalsMovement,
  engagement,
}) {
  const isMovement = pinnedNode.type === "movement";
  const testidBase = pinnedNode.type === "movement" ? `pinned-movement-${pinnedNode.key}` : `pinned-dynamic-${pinnedNode.key}`;
  // For dynamics, the roadmap keys are connections|storytelling; the map
  // key for Leadership & Storytelling is "leadership". Reconcile.
  const lensKey = isMovement ? null : (pinnedNode.key === "leadership" ? "storytelling" : pinnedNode.key);

  // Pull the canonical entity — phase or lens
  const phase = isMovement ? (roadmap?.phases || []).find((p) => p.key === pinnedNode.key) : null;
  const lens = !isMovement ? ((roadmap?.lenses || {})[lensKey] || {}) : null;
  const dynamic = !isMovement ? CONTINUOUS_DYNAMICS[pinnedNode.key] : null;
  const entity = isMovement ? phase : lens;

  const reflections = (entity?.reflections || []);
  const currentReflection = entity?.where_we_are_now || "";
  const currentUpdatedAt = entity?.where_we_are_now_updated_at;
  const currentUpdatedBy = entity?.where_we_are_now_updated_by;
  const historyCount = (entity?.reflection_history || []).length;
  // Persisted digest provenance — set by the current-reflection PATCH
  // when the advisor explicitly promotes an AI-drafted body. Drives the
  // subtle "AI-drafted · Advisor-approved" chip in the read view.
  const currentGeneratedBy = entity?.where_we_are_now_generated_by || null;
  const currentSourceIds = entity?.where_we_are_now_source_reflection_ids || [];

  // Roman numeral for the movement (I–IV) — pulled from the phase's index
  // in the canonical order.
  const romanForMovement = (() => {
    if (!isMovement) return null;
    const order = ["listen", "reveal", "reimagine", "guide"];
    const idx = order.indexOf(pinnedNode.key);
    return ["I", "II", "III", "IV"][idx] || "";
  })();

  // ── State hooks — declared unconditionally to satisfy hooks rules ──
  const [jotDraft, setJotDraft] = useState("");
  const [jotVisibility, setJotVisibility] = useState("team");
  const [savingJot, setSavingJot] = useState(false);
  const [savingCurrent, setSavingCurrent] = useState(false);
  const [editingCurrent, setEditingCurrent] = useState(false);
  const [currentDraft, setCurrentDraft] = useState("");
  const [confirmSnapshot, setConfirmSnapshot] = useState(false);
  const [promoteFromId, setPromoteFromId] = useState(null);

  // ── Phase B · Progressive prompts + Guided Reflection state ──
  // openGroupingKey: which of the four groupings is currently expanded
  //   (at most one at a time — a calm, editorial disclosure pattern).
  // groupingDrafts: per-grouping textarea draft, keyed by grouping key.
  // guidedMode / guidedStep: optional "Walk me through all four →" flow
  //   that steps 0 → 3 through GROUPING_ORDER. Guided saves land in the
  //   Working Journal (provisional), NEVER in the Current Reflection.
  const [openGroupingKey, setOpenGroupingKey] = useState(null);
  const [groupingDrafts, setGroupingDrafts] = useState({});
  const [savingGrouping, setSavingGrouping] = useState(false);
  const [guidedMode, setGuidedMode] = useState(false);
  const [guidedStep, setGuidedStep] = useState(0);

  // ── Phase C · Guided Reflection Digest state ──
  // When the advisor clicks "Summarize what I said →", we call the
  // digest endpoint and pre-fill the Current Reflection editor with an
  // AI-drafted paragraph. The draft is CLEARLY labelled AI-drafted and
  // MUST be reviewed + explicitly promoted before it becomes canon.
  // `digestSources` links the draft back to the guided Working Journal
  // entries that seeded it — persisted on promote for audit trail.
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestSources, setDigestSources] = useState([]);
  const [digestGeneratedBy, setDigestGeneratedBy] = useState(null); // null | "yadidai"
  const [digestGeneratedAt, setDigestGeneratedAt] = useState(null);

  // ── Migrated from retired MovementWorkspace / LensWorkspace ──
  // Next-questions composer (movement pin) + Working-notes editor
  // (lens pin) + shared save spinners. Declared here so React's Rules of
  // Hooks stays happy across every pin type.
  const [nqDraft, setNqDraft] = useState("");
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [workingNotes, setWorkingNotes] = useState("");
  const [savingLensNotes, setSavingLensNotes] = useState(false);

  // ── Iter 81.1 · Field questions surfaced inside the phase's own
  // "Next Questions We're Holding" section. Read-only aggregation
  // over notes.meeting_structured.open_questions[] filtered by the
  // pinned movement. Nothing is copied into the roadmap; each row
  // deep-links back to its source note and carries its own resolve
  // state on the source Note itself.
  const [fieldQuestions, setFieldQuestions] = useState([]);
  const [fieldLoading, setFieldLoading] = useState(false);
  const loadFieldQuestions = async () => {
    if (!isMovement || !engagementId || !pinnedNode?.key) {
      setFieldQuestions([]);
      return;
    }
    setFieldLoading(true);
    try {
      const { data } = await api.get(
        `/engagements/${engagementId}/open-questions?movement=${pinnedNode.key}&resolved=false&include_held=false`,
      );
      setFieldQuestions(data?.field || []);
    } catch {
      setFieldQuestions([]);
    } finally {
      setFieldLoading(false);
    }
  };
  useEffect(() => { loadFieldQuestions(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [engagementId, isMovement, pinnedNode?.key]);

  const resolveFieldQuestion = async (row) => {
    try {
      await api.post(`/notes/${row.note_id}/open-questions/${row.idx}/resolve`, { resolved: true });
      toast.success("Question marked resolved on source note");
      loadFieldQuestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed");
    }
  };

  // ── Iter 61 · One-tap PDF export of the Reflection Canvas ──
  // The article root is captured by the shared Yadid document pipeline
  // (see /app/frontend/src/lib/yadidDoc/). During capture we add
  // `.pdf-exporting` on the root so the bounded reading viewport
  // un-clips and every reflection + journal entry is included in the
  // PDF, then remove it so the interactive UI returns to bounded mode.
  //
  // Iter 85.29 · Reflection PDF now inherits the Yadid document
  // standard (Letter page, 22/15 mm safe margins, running header on
  // pages 2+, running "PRIVILEGED & CONFIDENTIAL · Page X of N · ©
  // YEAR YADID ADVISORY" footer, DOC_INSET alignment) via
  // `renderYadidPdf` from `@/lib/yadidDoc` instead of a bespoke
  // html2pdf setup.
  const articleRootRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const exportReflectionPdf = async () => {
    const el = articleRootRef.current;
    if (!el || exportingPdf) return;
    setExportingPdf(true);
    el.classList.add("pdf-exporting");
    // Give the browser a beat to apply the un-clip styles before capture.
    await new Promise((r) => setTimeout(r, 60));
    try {
      const { renderYadidPdf } = await import("@/lib/yadidDoc");
      const nodeLabel = pinnedNode.key.charAt(0).toUpperCase() + pinnedNode.key.slice(1);
      const engName = (engagement?.name || "engagement").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const today = new Date().toISOString().slice(0, 10);
      const filename = `Reflection_${engName}_${nodeLabel}_${today}.pdf`;
      // Reflection is a partner-authored working artefact — show the
      // firm wordmark from page 1 so partners immediately recognise it
      // as a Yadid document even without an in-content masthead.
      await renderYadidPdf({ element: el, filename, showRunningHeaderOnPageOne: true });
      toast.success("Reflection PDF exported");
    } catch (e) {
      toast.error(e?.message || "PDF export failed");
    } finally {
      el.classList.remove("pdf-exporting");
      setExportingPdf(false);
    }
  };

  // ── Signals · Context Layer merge (Feb 2026) ──
  // Signals from the Field is now the canonical contextual-intelligence
  // section. It renders for BOTH movement and dynamic pins, and its data
  // draws from TWO endpoints combined:
  //   · /method-signals   — notes, open_tasks, upcoming_milestones
  //                          (fetched by parent; movement-filterable
  //                          for movement pins; unfiltered for dynamics)
  //   · /roadmap/context-layer/{scope}/{key}
  //                        — decisions + prior_reflections
  //                          (node-scoped; backend applies permission
  //                          filtering for prior_reflections)
  // The two sources are combined at render time. Notes and Tasks are
  // primarily sourced from /method-signals (which powers the movement
  // filter chips); briefings/tasks from context-layer are dropped as
  // duplicate underlying records per the consolidation contract.
  const [contextData, setContextData] = useState(null);
  const [expandedSignal, setExpandedSignal] = useState({}); // colKey → bool

  useEffect(() => {
    let alive = true;
    setContextData(null);
    setExpandedSignal({});
    const scope = isMovement ? "phase" : "lens";
    const key = isMovement ? pinnedNode.key : lensKey;
    const fetchCtx = () => {
      api.get(`/engagements/${engagementId}/roadmap/context-layer/${scope}/${key}?limit=3`)
        .then((r) => { if (alive) setContextData(r.data); })
        .catch(() => { if (alive) setContextData({ counts: {}, all: {} }); });
    };
    fetchCtx();
    // Iter 85.65 · Same freshness fix as EngagementEmbraceTab's method-
    // signals — refetch when the browser tab regains visibility / focus
    // so decisions + prior reflections never lag behind newer records
    // written from other surfaces.
    const onFocus = () => fetchCtx();
    const onVisibility = () => { if (document.visibilityState === "visible") fetchCtx(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [engagementId, isMovement, pinnedNode.key, lensKey]);

  // Does the current viewer have guided journal entries here? Drives the
  // visibility of the "Summarize what I said →" affordance so it only
  // appears when there's actually material to synthesize. Declared here
  // with the other hooks so it always runs before any early return —
  // React Rules of Hooks require a stable call order across renders.
  const hasMyGuidedEntries = useMemo(() => {
    const email = currentUser?.email;
    if (!email) return false;
    return (reflections || []).some((r) =>
      r?.author_email === email &&
      String(r?.prompt || "").toLowerCase().endsWith("guided reflection")
    );
  }, [reflections, currentUser?.email]);

  // When the pinned node changes (advisor pins a different movement or
  // dynamic), reset the progressive-prompts surface so drafts and guided
  // state don't leak across nodes.
  useEffect(() => {
    setOpenGroupingKey(null);
    setGroupingDrafts({});
    setGuidedMode(false);
    setGuidedStep(0);
    setDigestSources([]);
    setDigestGeneratedBy(null);
    setDigestGeneratedAt(null);
    setNqDraft("");
  }, [pinnedNode?.type, pinnedNode?.key]);

  // ── Deep-link · `?entry=<reflection_id>` ──────────────────────────
  // Prior-Reflection rows in Signals from the Field navigate to
  // /engagements/{eid}?tab=embrace&movement=<k>&entry=<rid>. When we
  // land with an `entry` param, wait for the journal entry element to
  // exist in the DOM and scroll it into view with a subtle brass ring
  // for two seconds so the advisor sees the exact underlying evidence.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const entryId = params.get("entry");
    if (!entryId) return;
    // Reflections load with the roadmap; wait a tick for the DOM to
    // render the new journal-entry testid before scrolling.
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const el = document.querySelector(`[data-testid="${testidBase}-journal-entry-${entryId}"]`);
      if (el || attempts > 20) {
        clearInterval(timer);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-brass", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-brass", "ring-offset-2"), 2000);
          // Strip param so a reload doesn't re-scroll indefinitely.
          const url = new URL(window.location.href);
          url.searchParams.delete("entry");
          window.history.replaceState({}, "", url.toString());
        }
      }
    }, 150);
    return () => clearInterval(timer);
  }, [testidBase, reflections.length]);

  // Early-out guards AFTER all hooks so React's hook order stays stable.
  if (isMovement && !phase) return null;
  if (!isMovement && !dynamic) return null;

  const openCurrentEditor = (fromId = null, seedBody = null) => {
    setCurrentDraft(seedBody !== null ? seedBody : currentReflection);
    setEditingCurrent(true);
    setConfirmSnapshot(false);
    setPromoteFromId(fromId);
    // Manual open always resets digest provenance — the advisor is
    // authoring, not promoting an AI draft.
    setDigestSources([]);
    setDigestGeneratedBy(null);
    setDigestGeneratedAt(null);
  };

  // Cancel the current-reflection editor cleanly. Drops the draft, any
  // promote-from-id, and any in-flight digest provenance so the next
  // open of the editor starts from a clean slate.
  const cancelEdit = () => {
    setEditingCurrent(false);
    setConfirmSnapshot(false);
    setPromoteFromId(null);
    setDigestSources([]);
    setDigestGeneratedBy(null);
    setDigestGeneratedAt(null);
  };

  // ── Endpoint helpers — abstract phase vs lens ─────────────────────────
  const scopePath = isMovement ? `phase/${pinnedNode.key}` : `lens/${lensKey}`;

  const saveJournalEntry = async () => {
    const body = (jotDraft || "").trim();
    if (!body) { toast.error("Add a thought before saving."); return; }
    setSavingJot(true);
    try {
      const { data } = await api.post(
        `/engagements/${engagementId}/roadmap/${scopePath}/reflection`,
        { prompt: "", response: body, visibility: jotVisibility },
      );
      setRoadmap(data);
      setJotDraft("");
      toast.success("Saved to Working Journal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingJot(false); }
  };
  // ⌘⏎ / Ctrl-Enter shortcut inside the composer — cheap-and-fast save.
  const onJotKeyDown = (ev) => {
    if ((ev.metaKey || ev.ctrlKey) && ev.key === "Enter") {
      ev.preventDefault();
      saveJournalEntry();
    }
  };

  const commitCurrent = async () => {
    const body = (currentDraft || "").trim();
    if (!body) { toast.error("Current reflection needs a body."); return; }
    setSavingCurrent(true);
    try {
      const payload = { body };
      if (promoteFromId) payload.from_reflection_id = promoteFromId;
      // Phase C provenance — carried forward only when the draft is
      // being promoted with digest sources still attached. If the advisor
      // edited beyond recognition, the sources still stay linked because
      // the paragraph originated from them; the advisor's edit is
      // implicit review + approval.
      if (digestGeneratedBy) {
        payload.generated_by = digestGeneratedBy;
        if (digestSources.length) payload.source_reflection_ids = digestSources;
      }
      const { data } = await api.patch(
        `/engagements/${engagementId}/roadmap/${scopePath}/current-reflection`,
        payload,
      );
      setRoadmap(data);
      setEditingCurrent(false);
      setConfirmSnapshot(false);
      setPromoteFromId(null);
      setDigestSources([]);
      setDigestGeneratedBy(null);
      setDigestGeneratedAt(null);
      toast.success(promoteFromId ? "Promoted to current reflection" : "Current reflection updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSavingCurrent(false); }
  };

  // ── Progressive prompts / Guided Reflection handlers ─────────────────
  // Save the current grouping's draft as a Working Journal entry. We tag
  // the entry's `prompt` with "<Label> — guided reflection" so the origin
  // is traceable in the stream even though the composer stays lightweight.
  // Guided saves never touch `where_we_are_now`; promotion to the Current
  // Reflection remains an explicit, separate act by the advisor.
  const saveGroupingEntry = async (g) => {
    const body = (groupingDrafts[g.key] || "").trim();
    if (!body) { toast.error("Write something before saving."); return; }
    setSavingGrouping(true);
    try {
      const prompt = guidedMode
        ? `${g.label} · guided reflection`
        : `${g.label} · reflection`;
      const { data } = await api.post(
        `/engagements/${engagementId}/roadmap/${scopePath}/reflection`,
        { prompt, response: body, visibility: "team" },
      );
      setRoadmap(data);
      setGroupingDrafts((s) => ({ ...s, [g.key]: "" }));
      toast.success(`Saved to Working Journal · ${g.label}`);
      if (guidedMode) advanceGuidedStep();
      else setOpenGroupingKey(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingGrouping(false); }
  };

  const enterGuidedMode = () => {
    setGuidedMode(true);
    setGuidedStep(0);
    setOpenGroupingKey(GROUPING_ORDER[0].key);
  };
  const exitGuidedMode = () => {
    setGuidedMode(false);
    setGuidedStep(0);
  };
  const advanceGuidedStep = () => {
    const next = guidedStep + 1;
    if (next >= GROUPING_ORDER.length) {
      setGuidedMode(false);
      setGuidedStep(0);
      setOpenGroupingKey(null);
      toast.success("Guided reflection complete — nothing overwritten. Entries saved to Working Journal.");
    } else {
      setGuidedStep(next);
      setOpenGroupingKey(GROUPING_ORDER[next].key);
    }
  };
  const skipGuidedStep = () => advanceGuidedStep();
  const backGuidedStep = () => {
    if (guidedStep === 0) return;
    const prev = guidedStep - 1;
    setGuidedStep(prev);
    setOpenGroupingKey(GROUPING_ORDER[prev].key);
  };

  const cycleVisibility = async (rid, cur) => {
    const order = ["private", "team", "firm"];
    const nextV = order[(order.indexOf(cur || "team") + 1) % order.length];
    try {
      const { data } = await api.patch(
        `/engagements/${engagementId}/roadmap/${scopePath}/reflection/${rid}/visibility`,
        { visibility: nextV },
      );
      setRoadmap(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Visibility change failed");
    }
  };

  // Delete a Working Journal entry. Migrated from the retired
  // Movement/Lens workspaces. Uses the pre-existing DELETE endpoints;
  // frontend restricts the affordance to the author for governance
  // consistency with the visibility-cycle rule.
  const removeReflection = async (rid) => {
    try {
      const { data } = await api.delete(
        `/engagements/${engagementId}/roadmap/${scopePath}/reflection/${rid}`,
      );
      setRoadmap(data);
      toast.success("Entry removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ── Next Questions handlers (movement pin only) ─────────────────────
  const patchMovementWorkspace = async (patch) => {
    setSavingWorkspace(true);
    try {
      const { data } = await api.patch(
        `/engagements/${engagementId}/roadmap/phase/${pinnedNode.key}/workspace`,
        patch,
      );
      setRoadmap(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingWorkspace(false); }
  };
  const nextQs = isMovement ? (phase?.next_questions || []) : [];
  const addNextQ = async () => {
    const t = nqDraft.trim();
    if (!t) return;
    await patchMovementWorkspace({ next_questions: [...nextQs, t] });
    setNqDraft("");
  };
  const removeNextQ = async (idx) => {
    const nextList = nextQs.filter((_, i) => i !== idx);
    await patchMovementWorkspace({ next_questions: nextList });
  };

  // ── Working Notes handler (lens pin only) ───────────────────────────
  const saveWorkingNotes = async () => {
    setSavingLensNotes(true);
    try {
      const { data } = await api.patch(
        `/engagements/${engagementId}/roadmap/lens/${lensKey}`,
        { notes: workingNotes },
      );
      setRoadmap(data);
      toast.success("Working notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingLensNotes(false); }
  };

  // ── Phase C · Guided Reflection Digest ──────────────────────────────
  // Ask YadidAi to draft a candidate Current Reflection from this user's
  // recent guided Working Journal entries. The endpoint is read-only:
  // nothing on the roadmap changes here. The advisor still has to
  // explicitly promote via Review & snapshot.
  const runDigest = async () => {
    setDigestLoading(true);
    try {
      const { data } = await api.post(
        `/engagements/${engagementId}/roadmap/${scopePath}/reflection-digest`,
      );
      setCurrentDraft(data.draft_body || "");
      setDigestSources(data.source_reflection_ids || []);
      setDigestGeneratedBy(data.generated_by || "yadidai");
      setDigestGeneratedAt(data.generated_at || null);
      setEditingCurrent(true);
      setConfirmSnapshot(false);
      setPromoteFromId(null);
      toast.success(`YadidAi drafted a candidate from ${data.source_count || 0} entr${(data.source_count === 1) ? "y" : "ies"} — review before promoting.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest unavailable");
    } finally { setDigestLoading(false); }
  };

  // Does the current viewer have guided journal entries here? (Hook
  // moved above to run before early returns — see block after digest
  // state declarations.)

  // ── Cross-surface Context Ribbon detection ────────────────────────────
  // The Client and Briefing Studio surfaces launch here via the same URL
  // scheme `/engagements/{id}?tab=embrace&movement=…&from=…`. When `from`
  // is present, we render an editorial ribbon with the lineage + return
  // path so the advisor is oriented and can go back with one click.
  const [ribbonSearchParams] = [new URLSearchParams(window.location.search)];
  const fromSurface = ribbonSearchParams.get("from");    // "client" | "briefing" | null
  const fromClientName = ribbonSearchParams.get("from_client_name") || "";
  const fromBriefingTitle = ribbonSearchParams.get("from_briefing_title") || "";

  const framingLabel = isMovement
    ? `${romanForMovement} · ${phase.framework_label || ""} — ${phase.mindset || ""}`.trim().replace(/[·—]\s*$/, "")
    : `Continuous dynamic · ${dynamic?.shortLabel || ""}`;
  const headlineText = isMovement ? (phase.client_label || phase.framework_label) : (dynamic?.framingQuestion || dynamic?.shortLabel);
  const framingQuestion = isMovement
    ? (Array.isArray(phase.questions) && phase.questions.length ? phase.questions[0] : phase.intent)
    : (dynamic?.explainer || "");
  return (
    <div
      ref={articleRootRef}
      className="mt-6 border border-primary/30 bg-card animate-fade-up"
      data-testid={testidBase}
      role="region"
      aria-labelledby={`${testidBase}-heading`}
    >
      {/*
        Iter 61 — When exporting to PDF we un-clip the bounded reading
        viewport so ALL Current Reflection + Working Journal content is
        captured, not just the visible slice. The class is toggled on
        just for the duration of html2pdf's capture pass and removed
        immediately after so the interactive UI stays bounded.
      */}
      <style>{`
        .pdf-exporting .rca-scroll-clip { height: auto !important; max-height: none !important; overflow: visible !important; }
        .pdf-exporting [data-scroll-column] { overflow: visible !important; max-height: none !important; }
        .pdf-exporting { background: #ffffff !important; }
        .pdf-exporting .pdf-hide { display: none !important; }
      `}</style>
      {/* Editorial hairline top-rule — continuous dynamic gets brass */}
      <div className={isMovement ? "h-px bg-primary/60" : "h-px bg-brass/70"} aria-hidden />

      {/* Context Ribbon — appears only when opened from a non-engagement surface */}
      {fromSurface && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 px-5 py-2 bg-secondary/40 border-b border-primary/40"
          data-testid={`${testidBase}-context-ribbon`}
        >
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            {fromClientName && <>Partner Org · <span className="text-foreground">{fromClientName}</span> · </>}
            {fromBriefingTitle && <>Briefing · <span className="text-foreground">{fromBriefingTitle}</span> · </>}
            <span className="text-primary">Method · {isMovement ? (phase.label || phase.client_label || phase.key.charAt(0).toUpperCase() + phase.key.slice(1)) : dynamic?.shortLabel}</span>
          </div>
          <button
            type="button"
            className="text-[0.625rem] font-mono uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1 hover:bg-primary/90"
            onClick={() => {
              // The ribbon's own "Return" simply keeps the advisor here —
              // they ARE on the originating engagement now. Instead we
              // clear the from=… params so the ribbon disappears.
              const url = new URL(window.location.href);
              ["from", "from_client_name", "from_briefing_title"].forEach(k => url.searchParams.delete(k));
              window.history.replaceState({}, "", url.toString());
              window.location.reload();
            }}
          >
            Dismiss context ↗
          </button>
        </div>
      )}

      {/* ══════ HEADLINE BLOCK ══════ */}
      <div className="px-5 md:px-8 py-6 md:py-8 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary">
            § {framingLabel}
          </div>
          <h4
            id={`${testidBase}-heading`}
            className="mt-2 font-bold tracking-tight text-foreground"
            style={{ fontFamily: "'Playfair Display', 'Merriweather', Georgia, serif", fontSize: "1.75rem", lineHeight: 1.1, fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            {headlineText}
          </h4>
          {framingQuestion && (
            <p className="mt-3 text-sm md:text-base text-muted-foreground italic border-l-2 border-brass/40 pl-3 max-w-2xl" style={{ fontFamily: "'Merriweather', serif", lineHeight: 1.55 }}>
              {framingQuestion}
            </p>
          )}
          {/* Quiet inline invitation into Guided Reflection — sits beneath
              the framing question in the same serif family so it reads
              like an editorial pull-quote link, not a call-to-action pill.
              Hidden while a guided walk is already in progress to avoid
              redundancy; shown again on exit or completion. */}
          {!guidedMode && (
            <button
              type="button"
              onClick={enterGuidedMode}
              data-testid={`${testidBase}-walk-through`}
              className="mt-3 inline-block text-sm italic text-brass hover:text-primary transition-colors border-b border-transparent hover:border-primary pb-0.5"
              style={{ fontFamily: "'Cormorant Garamond', 'Merriweather', serif", fontWeight: 500 }}
            >
              Walk me through all four →
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0 pdf-hide">
          <button
            type="button"
            onClick={exportReflectionPdf}
            disabled={exportingPdf}
            aria-label="Export reflection as PDF"
            data-testid={`pinned-export-pdf-${pinnedNode.key}`}
            className="inline-flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-[0.24em] text-brass hover:text-primary border border-brass/40 hover:border-primary px-2.5 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            <FilePdf size={13} weight="regular" />
            {exportingPdf ? "Preparing…" : "Export PDF"}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close reflection article"
            data-testid={`pinned-close-${pinnedNode.key}`}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* ══════ PROGRESSIVE PROMPTS · four canonical groupings ══════
          Extracted to <ProgressivePromptsStrip/> after Phase B. The strip
          renders between the framing block and the governed Current
          Reflection. All state + handlers still live here so the digest
          feature (Phase C) can inspect the same source of truth. */}
      <ProgressivePromptsStrip
        testidBase={testidBase}
        pinnedNodeType={pinnedNode.type}
        pinnedNodeKey={pinnedNode.key}
        isMovement={isMovement}
        openGroupingKey={openGroupingKey}
        setOpenGroupingKey={setOpenGroupingKey}
        groupingDrafts={groupingDrafts}
        setGroupingDrafts={setGroupingDrafts}
        savingGrouping={savingGrouping}
        guidedMode={guidedMode}
        guidedStep={guidedStep}
        saveGroupingEntry={saveGroupingEntry}
        skipGuidedStep={skipGuidedStep}
        backGuidedStep={backGuidedStep}
        exitGuidedMode={exitGuidedMode}
      />

      {/* ══════ ARTICLE GRID — Current Reflection | Working Journal ══════
          Round-4 fidelity: the two disciplines sit as facing columns of
          an editorial article, separated by a hairline. Left column
          (Current Reflection) is anchored on Navy; right column
          (Working Journal) is anchored on Brass. On mobile the columns
          stack. Parent still owns state; child components accept a
          columnMode flag to render as a single article column.

          Feb 2026 · Iter 54 · Bounded reading viewport:
          On md+ the grid gets a viewport-relative fixed height
          (`clamp(28rem, calc(100vh - 22rem), 44rem)`). Each column is
          independently scrollable so long reflection history OR many
          journal entries can't push Signals below the fold or make one
          side taller than the other. `minmax(0, N)` tracks + `min-h-0`
          on the grid and its children are required so the scroll
          containment actually engages (default `min-height: auto`
          would let a tall child force the row taller than the clamp).
          Mobile keeps the natural stacked flow — no forced height. */}
      <div
        className="rca-scroll-clip border-t-2 border-double border-border/70 grid grid-cols-1 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md:divide-x md:divide-border min-h-0 md:h-[clamp(28rem,calc(100vh-22rem),44rem)]"
        data-testid={`${testidBase}-canvas-grid`}
      >
        <CurrentReflectionBlock
          columnMode
          testidBase={testidBase}
          currentReflection={currentReflection}
          currentUpdatedAt={currentUpdatedAt}
          currentUpdatedBy={currentUpdatedBy}
          historyCount={historyCount}
          history={entity?.reflection_history || []}
          currentGeneratedBy={currentGeneratedBy}
          currentSourceIds={currentSourceIds}
          reflections={reflections}
          editingCurrent={editingCurrent}
          currentDraft={currentDraft}
          setCurrentDraft={setCurrentDraft}
          confirmSnapshot={confirmSnapshot}
          setConfirmSnapshot={setConfirmSnapshot}
          savingCurrent={savingCurrent}
          digestGeneratedBy={digestGeneratedBy}
          digestSources={digestSources}
          digestLoading={digestLoading}
          hasMyGuidedEntries={hasMyGuidedEntries}
          onOpenEditor={openCurrentEditor}
          onCancelEdit={cancelEdit}
          onRunDigest={runDigest}
          onCommit={commitCurrent}
        />
        <WorkingJournalStream
          columnMode
          testidBase={testidBase}
          pinnedNodeKey={pinnedNode.key}
          isMovement={isMovement}
          reflections={reflections}
          currentUser={currentUser}
          jotDraft={jotDraft}
          setJotDraft={setJotDraft}
          jotVisibility={jotVisibility}
          setJotVisibility={setJotVisibility}
          savingJot={savingJot}
          onSaveJot={saveJournalEntry}
          onJotKeyDown={onJotKeyDown}
          onCycleVisibility={cycleVisibility}
          onPromoteFromDraft={(text) => openCurrentEditor(null, text)}
          onPromoteEntry={(id, body) => openCurrentEditor(id, body)}
          onDeleteEntry={removeReflection}
          onOpenBriefing={onOpenBriefing}
        />
      </div>

      {/* ══════ § Signals from the field — canonical contextual intelligence ══════
          Feb 2026 · The former Context Layer has been merged into this
          section. Five columns render for movement pins (Notes &
          briefings · Open tasks · Milestones · Decisions · Prior
          reflections); dynamic pins drop Milestones (Guide-scoped) and
          the movement filter chips (dynamics span all movements). Every
          row opens the original source. Prior Reflections are permission-
          filtered on the backend (`_reflection_visible_to`). Reasoning
          order for the advisor: reflect / capture → examine what
          surrounds the reflection → decide what questions we're still
          holding. */}
      <section
        className="px-6 md:px-10 py-6 md:py-8 border-t border-border bg-secondary/10"
        data-testid={`${testidBase}-signals`}
      >
        <div className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
          <div className="text-[0.6875rem] font-mono uppercase tracking-[0.28em] text-brass flex items-baseline gap-3">
            <span>§ Signals from the field</span>
            <span aria-hidden className="inline-block w-8 h-px bg-border" />
          </div>
          {isMovement && (
            <div className="flex items-center gap-1 flex-wrap" data-testid={`${testidBase}-signals-filter`}>
              <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground mr-1">Filter</span>
              <SignalChip active={!signalsMovement} onClick={() => setSignalsMovement && setSignalsMovement(null)} testid={`${testidBase}-signals-filter-all`}>All</SignalChip>
              {["listen","reveal","reimagine","guide"].map((m) => (
                <SignalChip key={m} active={signalsMovement === m} onClick={() => setSignalsMovement && setSignalsMovement(m)} testid={`${testidBase}-signals-filter-${m}`}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </SignalChip>
              ))}
            </div>
          )}
        </div>
        <p
          className="text-[0.8125rem] italic text-muted-foreground max-w-2xl mb-4"
          style={{ fontFamily: "'Merriweather', serif" }}
          data-testid={`${testidBase}-signals-subtitle`}
        >
          {NODE_SIGNALS_CAPTION[pinnedNode.key] || DEFAULT_SIGNALS_CAPTION}
        </p>
        <div
          className={`grid grid-cols-1 md:grid-cols-2 ${isMovement ? "xl:grid-cols-5" : "xl:grid-cols-4"} gap-3`}
        >
          <SignalColumn
            colKey="notes"
            icon={NotePencil}
            label="Notes & Briefings"
            all={signals?.notes || []}
            expanded={expandedSignal.notes}
            onToggle={() => setExpandedSignal((s) => ({ ...s, notes: !s.notes }))}
            emptyCopy={isMovement ? "(no notes match this lens)" : "(no notes for this engagement)"}
            testid={`${testidBase}-signals-col-notes`}
            renderMeta={(n) => (n.created_at || "").slice(0, 10)}
            titleOf={(n) => n.title || "Untitled note"}
            hrefOf={(n) => `/notes?open=${n.id}`}
            previewOf={(n) => ({
              kind: (n.note_type || "note").replace(/_/g, " "),
              body: n.preview || "",
            })}
          />
          <SignalColumn
            colKey="tasks"
            icon={ListChecks}
            label="Open Tasks"
            all={signals?.open_tasks || []}
            expanded={expandedSignal.tasks}
            onToggle={() => setExpandedSignal((s) => ({ ...s, tasks: !s.tasks }))}
            emptyCopy={isMovement ? "(no tasks match this lens)" : "(no open tasks)"}
            testid={`${testidBase}-signals-col-tasks`}
            renderMeta={(t) => `${t.priority || "medium"}${t.due_date ? ` · ${t.due_date}` : ""}`}
            titleOf={(t) => t.title}
            hrefOf={(t) => `/tasks?open=${t.id}`}
            previewOf={(t) => ({
              kind: `${(t.status || "todo").replace(/_/g, " ")} · ${(t.priority || "medium")}`,
              body: t.description || "",
            })}
          />
          {isMovement && (
            <SignalColumn
              colKey="milestones"
              icon={Flag}
              label="Milestones"
              all={signals?.upcoming_milestones || []}
              expanded={expandedSignal.milestones}
              onToggle={() => setExpandedSignal((s) => ({ ...s, milestones: !s.milestones }))}
              emptyCopy={signalsMovement && signalsMovement !== "guide"
                ? "(milestones surface under Guide)"
                : "(no upcoming milestones)"}
              testid={`${testidBase}-signals-col-milestones`}
              renderMeta={(m) => m.due_date || ""}
              titleOf={(m) => m.title}
              hrefOf={(m) => m.href || null}
              previewOf={(m) => ({
                kind: m.due_date ? `due ${m.due_date}` : "milestone",
                body: m.description || "",
              })}
            />
          )}
          <SignalColumn
            colKey="decisions"
            icon={ScribbleLoop}
            label="Decisions"
            all={contextData?.all?.decisions || []}
            expanded={expandedSignal.decisions}
            onToggle={() => setExpandedSignal((s) => ({ ...s, decisions: !s.decisions }))}
            emptyCopy="(no decisions recorded)"
            testid={`${testidBase}-signals-col-decisions`}
            renderMeta={(d) => [d.source_note_title, (d.decided_at || "").slice(0, 10)].filter(Boolean).join(" · ")}
            titleOf={(d) => d.text}
            hrefOf={(d) => d.href}
            previewOf={(d) => ({
              kind: d.source_note_title ? `Decision · ${d.source_note_title}` : "Decision",
              body: d.text || "",
            })}
          />
          <SignalColumn
            colKey="prior_reflections"
            icon={Compass}
            label="Prior Reflections"
            all={contextData?.all?.prior_reflections || []}
            expanded={expandedSignal.prior_reflections}
            onToggle={() => setExpandedSignal((s) => ({ ...s, prior_reflections: !s.prior_reflections }))}
            emptyCopy="(no prior reflections)"
            testid={`${testidBase}-signals-col-prior-reflections`}
            renderMeta={(r) => [
              r.engagement_name,
              r.kind === "current_reflection" ? "Current reflection" : "Journal entry",
              (r.created_at || "").slice(0, 10),
            ].filter(Boolean).join(" · ")}
            titleOf={(r) => r.excerpt || "(empty)"}
            hrefOf={(r) => r.href}
            previewOf={(r) => ({
              kind: [
                r.engagement_name,
                r.kind === "current_reflection" ? "Current reflection" : "Journal entry",
              ].filter(Boolean).join(" · "),
              body: r.excerpt || "",
            })}
          />
        </div>
        {isMovement && (
          <div className="mt-2 text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
            {signalsMovement
              ? `Filtered by ${signalsMovement} · relevance lens — underlying records unchanged`
              : "Every row opens its original source · permission-filtered"}
          </div>
        )}
        {!isMovement && (
          <div className="mt-2 text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
            Every row opens its original source · permission-filtered
          </div>
        )}
      </section>

      {/* ══════ § Next questions — movement pin only ══════
          Governed by `roadmap.phases[k].next_questions` (advisor-held)
          via PATCH …/workspace, PLUS field-derived open questions
          aggregated in-place from every organized Note whose
          meeting_structured.open_questions[i].movement matches this
          phase (Iter 81.1). Field rows are read-only rollups that
          deep-link to the source note and can be resolved without
          copying into the roadmap; advisor-held rows keep their
          existing edit/delete behaviour. */}
      {isMovement && (
        <section
          className="px-6 md:px-10 py-6 md:py-8 border-t border-border"
          data-testid={`${testidBase}-nextq`}
        >
          <div className="text-[0.6875rem] font-mono uppercase tracking-[0.28em] text-brass mb-3 flex items-baseline gap-3">
            <span>§ Next questions we're holding</span>
            <span aria-hidden className="flex-1 h-px bg-border" />
          </div>
          <ul className="space-y-2 mb-3 max-w-2xl">
            {nextQs.map((q, i) => (
              <li key={`held-${i}::${q}`} className="flex items-start gap-2 group text-sm" data-testid={`${testidBase}-nextq-item-${i}`}>
                <span className="text-primary shrink-0 mt-1" aria-hidden>→</span>
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: "'Merriweather', serif" }}>{q}</div>
                  <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                    Advisor-held
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeNextQ(i)}
                  className="opacity-40 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1"
                  aria-label="Remove"
                  data-testid={`${testidBase}-nextq-remove-${i}`}
                >
                  <X size={11} />
                </button>
              </li>
            ))}
            {fieldQuestions.map((row) => (
              <li
                key={`field-${row.note_id}-${row.idx}`}
                className="flex items-start gap-2 group text-sm border-l border-brass/40 pl-2 -ml-3"
                data-testid={`${testidBase}-nextq-field-${row.note_id}-${row.idx}`}
              >
                <span className="text-brass shrink-0 mt-1" aria-hidden>◇</span>
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: "'Merriweather', serif" }}>{row.text}</div>
                  <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-brass/90 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>From meeting</span>
                    <span aria-hidden className="opacity-40">·</span>
                    <Link
                      to={`/notes?open=${row.note_id}`}
                      className="text-brass hover:text-primary"
                      data-testid={`${testidBase}-nextq-field-${row.note_id}-${row.idx}-open`}
                      title={row.note_title || "Open source note"}
                    >
                      {(row.note_title || "note").slice(0, 40)}
                    </Link>
                    {row.note_date && (
                      <>
                        <span aria-hidden className="opacity-40">·</span>
                        <span className="text-muted-foreground">{String(row.note_date).slice(0, 10)}</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => resolveFieldQuestion(row)}
                  className="opacity-40 group-hover:opacity-100 text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-emerald-500 p-1"
                  data-testid={`${testidBase}-nextq-field-${row.note_id}-${row.idx}-resolve`}
                  title="Mark resolved on the source note — original question preserved"
                >
                  Resolve
                </button>
              </li>
            ))}
            {nextQs.length === 0 && fieldQuestions.length === 0 && !fieldLoading && (
              <li className="text-xs italic text-muted-foreground/70">
                (nothing on the working list yet — add one below, or capture more in an organized meeting note)
              </li>
            )}
          </ul>
          <div className="flex gap-2 max-w-2xl">
            <Input
              value={nqDraft}
              onChange={(e) => setNqDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNextQ(); } }}
              placeholder="Add a question…"
              className="rounded-none bg-secondary/40 flex-1 h-9"
              data-testid={`${testidBase}-nextq-input`}
            />
            <Button
              size="sm"
              onClick={addNextQ}
              disabled={savingWorkspace || !nqDraft.trim()}
              className="rounded-none bg-primary text-primary-foreground h-9 text-[0.6875rem] font-mono uppercase tracking-widest"
              data-testid={`${testidBase}-nextq-add`}
            >
              <Plus size={12} weight="bold" className="mr-1" /> Add
            </Button>
          </div>
        </section>
      )}

      {/* ══════ § Working notes — dynamic pin only ══════
          Governed by `roadmap.lenses[k].notes` via PATCH …/lens/{k}. */}
      {!isMovement && (
        <section
          className="px-6 md:px-10 py-6 md:py-8 border-t border-border"
          data-testid={`${testidBase}-lens-notes`}
        >
          <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
            <div className="text-[0.6875rem] font-mono uppercase tracking-[0.28em] text-brass flex items-baseline gap-3">
              <span>§ Working notes</span>
              <span aria-hidden className="inline-block w-8 h-px bg-border" />
            </div>
            <Button
              size="sm"
              onClick={saveWorkingNotes}
              disabled={savingLensNotes || workingNotes === (entity?.notes || "")}
              className="rounded-none bg-primary text-primary-foreground h-8 text-[0.6875rem] font-mono uppercase tracking-widest"
              data-testid={`${testidBase}-lens-notes-save`}
            >
              Save
            </Button>
          </div>
          {dynamic?.workingNotesPrompt && (
            <p className="text-xs italic text-muted-foreground mb-2 max-w-2xl" style={{ fontFamily: "'Merriweather', serif" }}>
              {dynamic.workingNotesPrompt}
            </p>
          )}
          <Textarea
            rows={4}
            value={workingNotes}
            onChange={(e) => setWorkingNotes(e.target.value)}
            placeholder="Anything else worth tracking on this lens for this engagement."
            className="rounded-none bg-secondary/40 font-serif max-w-2xl"
            data-testid={`${testidBase}-lens-notes-input`}
          />
        </section>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Internal helpers used by the merged Signals section. The Feb 2026
// consolidation absorbed the former Context Layer into Signals; the rich
// SignalColumn below supports progressive disclosure ("+ N more") and
// clickable rows that navigate to each record's original source.
// ─────────────────────────────────────────────────────────────────────────
const SIGNAL_HEAD_LIMIT = 3;

// Per-node Signals subtitle (Feb 2026 · Listen refinement). Each node
// gets a tailored caption that reflects the advisory task specific to
// it; nodes without an override fall back to the generic canvas copy.
// Keep captions here — one place — so the canonical Signals framing
// stays consistent across future node-specific refinements.
const DEFAULT_SIGNALS_CAPTION =
  "What surrounds this reflection — recent activity, decisions, commitments, and prior thinking shaping the engagement.";
const NODE_SIGNALS_CAPTION = {
  listen: "What we have already heard directly from the client — their words, priorities, and concerns as captured, before our interpretation.",
};

function SignalColumn({
  colKey, icon: Icon, label, all, expanded, onToggle,
  emptyCopy, testid, renderMeta, titleOf, hrefOf, previewOf,
}) {
  const items = Array.isArray(all) ? all : [];
  const total = items.length;
  const visible = expanded ? items : items.slice(0, SIGNAL_HEAD_LIMIT);
  const hidden = Math.max(0, total - SIGNAL_HEAD_LIMIT);
  return (
    <div className="border border-border/60 bg-card p-3" data-testid={testid}>
      {/* Iter 85.85 · Uniform column header: icon + label left-truncate,
          right-aligned tabular count for consistent alignment across
          all five signal columns at desktop widths. */}
      <div className="flex items-center gap-2 mb-2">
        <div className="min-w-0 flex-1 flex items-center gap-1.5 text-[0.5938rem] font-mono uppercase tracking-widest text-eyebrow">
          <Icon size={10} weight="fill" className="shrink-0" />
          <span className="truncate" title={label}>{label}</span>
        </div>
        <span
          className="text-[0.5938rem] font-mono tabular-nums text-muted-foreground shrink-0"
          data-testid={`${testid}-count`}
        >
          {total}
        </span>
      </div>
      <ul className="space-y-1.5">
        {visible.map((it, i) => (
          <SignalRow
            key={`${colKey}-${it.id ?? i}`}
            title={titleOf(it)}
            meta={renderMeta ? renderMeta(it) : ""}
            href={hrefOf ? hrefOf(it) : null}
            preview={previewOf ? previewOf(it) : null}
            testid={`${testid}-row-${i}`}
          />
        ))}
        {total === 0 && (
          <li className="text-[0.6875rem] italic text-muted-foreground/70">{emptyCopy}</li>
        )}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testid}-more`}
          className="mt-2 text-[0.5938rem] font-mono uppercase tracking-widest text-brass border-b border-transparent hover:border-brass pb-0.5"
        >
          {expanded ? "Show fewer ↑" : `+ ${hidden} more ↓`}
        </button>
      )}
    </div>
  );
}

function SignalRow({ title, meta, href, preview, testid }) {
  const inner = (
    <>
      <div className="text-[0.75rem] leading-tight text-foreground group-hover:text-primary transition-colors font-medium line-clamp-2">
        {title || "Untitled"}
      </div>
      {meta && (
        <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
          {meta}
        </div>
      )}
    </>
  );
  const isInternalLink = href && String(href).startsWith("/");
  const trigger = isInternalLink ? (
    <Link
      to={href}
      className="block group border-l-2 border-transparent hover:border-brass pl-2 transition-colors"
      data-testid={testid}
    >
      {inner}
    </Link>
  ) : (
    <div className="border-l-2 border-transparent pl-2" data-testid={testid}>
      {inner}
    </div>
  );
  // Iter 85.98 · Hover-preview for Signals rows. Only renders the popover
  // when there is a body snippet to show — otherwise falls back to the
  // plain row so an empty card never flashes. Delay opens the card only
  // after ~250ms so a mouse passing over the column doesn't spawn a
  // cascade of popovers.
  const hasPreview = preview && (preview.body || "").trim();
  if (!hasPreview) {
    return <li>{trigger}</li>;
  }
  return (
    <li>
      <HoverCard openDelay={200} closeDelay={80}>
        <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
        <HoverCardContent
          side="top"
          align="start"
          className="w-80 max-w-[24rem] p-3 space-y-1.5"
          data-testid={`${testid}-preview`}
        >
          {preview.kind && (
            <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-eyebrow">
              {preview.kind}
            </div>
          )}
          <div className="font-display text-sm leading-snug text-foreground">
            {title || "Untitled"}
          </div>
          <div className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap line-clamp-6">
            {preview.body}
          </div>
          {meta && (
            <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground/80 pt-1 border-t border-border/40">
              {meta}
            </div>
          )}
        </HoverCardContent>
      </HoverCard>
    </li>
  );
}

function SignalChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`text-[0.5625rem] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "border-border/70 text-muted-foreground hover:border-primary/60 hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

